import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { GraduationCap, User, X } from "lucide-react";
import PaginatorCst from "@components/PaginatorCst";
import DataTableCst, { DataTableColumn } from "@components/DataTableCst";
import useSWR from "swr";
import dayjs from "dayjs";
import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { EnrolledUserItem } from "../types";
import { formatEnrollTypeLabel } from "../config/enrollTypeMap";

interface EnrollmentHistoryModalProps {
  open: boolean;
  onClose: () => void;
  objectId: number;
  objectTitle?: string;
  user: EnrolledUserItem | null;
}

const PAGE_SIZE = 20;

const NO_EXPIRATION_SENTINEL = "9999-12-31";

const formatDate = (value?: string | null): string =>
  value && !String(value).startsWith(NO_EXPIRATION_SENTINEL)
    ? dayjs(value).format("DD/MM/YYYY")
    : "-";

const EnrollmentHistoryModal: React.FC<EnrollmentHistoryModalProps> = ({
  open,
  onClose,
  objectId,
  objectTitle,
  user,
}) => {
  const { t } = useTranslation("users-and-groups");
  const { t: tInput } = useTranslation("input");
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  useEffect(() => {
    if (open) setPage(1);
  }, [open, user?.userId]);

  const url =
    open && user?.userId
      ? `${ServicesURL.getEnrollHistory}?objectId=${objectId}&userId=${
          user.userId
        }&sortEventsByCreationTime=DESC&pageIndex=${page - 1}&pageSize=${pageSize}`
      : null;

  const { data, isLoading } = useSWR(url, (u: string) =>
    fetcher<any>(u, MethodType.GET),
  );

  const events: any[] = (data as any)?.eventsResponseList ?? [];
  const totalItems: number = (data as any)?.metadata?.totalElements ?? 0;
  const totalPages: number =
    (data as any)?.metadata?.totalPages ??
    Math.max(Math.ceil(totalItems / pageSize), 1);

  const optionLabel = (group: string, value?: string | null): string => {
    if (value === undefined || value === null || value === "") return "-";
    const key = `options.${group}.${String(value).toLowerCase()}`;
    return String(tInput(key, { defaultValue: String(value) }));
  };

  const fullName = user
    ? [user.userFirstName, user.userLastName].filter(Boolean).join(" ").trim()
    : "";

  const fieldLabel = (key: string): string =>
    String(t(`history-modal.fields.${key}`));

  const columns: DataTableColumn<any>[] = [
    {
      key: "eventType",
      label: fieldLabel("method"),
      render: (e) => optionLabel("event_type", e.eventType),
    },
    {
      key: "massiveGroupId",
      label: fieldLabel("group-id"),
      render: (e) =>
        e.massiveGroupId != null ? String(e.massiveGroupId) : "-",
    },
    {
      key: "channel",
      label: fieldLabel("channel"),
      render: (e) => optionLabel("channel_enroll", e.channel),
    },
    {
      key: "enrollType",
      label: fieldLabel("enroll-type"),
      render: (e) => formatEnrollTypeLabel(e.enrollType) || "-",
    },
    {
      key: "expirationDate",
      label: fieldLabel("expiration-date"),
      render: (e) => formatDate(e.expirationDate),
    },
    {
      key: "creationTime",
      label: fieldLabel("creation-date"),
      render: (e) => formatDate(e.creationTime),
    },
    {
      key: "updateTime",
      label: fieldLabel("update-date"),
      render: (e) => formatDate(e.updateTime),
    },
    {
      key: "completionTime",
      label: fieldLabel("completion-date"),
      render: (e) => formatDate(e.completionTime),
    },
    {
      key: "eventStatus",
      label: fieldLabel("event-status"),
      render: (e) => optionLabel("event_status", e.eventStatus),
    },
    {
      key: "expirationTypology",
      label: fieldLabel("expiration-type"),
      render: (e) => optionLabel("hard-soft-label", e.expirationTypology),
    },
    {
      key: "sessionUserId",
      label: String(t("history-modal.operator-id")),
      render: (e) => (e.sessionUserId != null ? String(e.sessionUserId) : "-"),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
          height: "800px",
        },
      }}
    >
      <DialogTitle sx={{ padding: "20px 24px 8px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                color: theme.palette.text.primary,
                fontSize: "14px",
              }}
            >
              <User size={16} />
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                {fullName || "-"}
              </Typography>
              {objectTitle && (
                <>
                  <Typography
                    sx={{ color: theme.palette.divider, fontSize: "14px" }}
                  >
                    |
                  </Typography>
                  <GraduationCap size={16} />
                  <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                    {objectTitle}
                  </Typography>
                </>
              )}
            </Box>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: theme.palette.text.primary,
                mt: 0.5,
              }}
            >
              {t("history-modal.title")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "12px 24px 24px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <DataTableCst<any>
          columns={columns}
          rows={events}
          getRowId={(_, index) => index}
          isLoading={isLoading}
          emptyLabel={String(t("history-modal.empty"))}
          plain
          stickyHeader
          containerSx={{ flex: 1, minHeight: 0, overflow: "auto" }}
        />

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <PaginatorCst
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnrollmentHistoryModal;
